import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, Button,
    Paper, CircularProgress, Snackbar, Alert,
    Divider, Card, CardContent, Stepper, Step, StepLabel, StepContent,
    Avatar, useMediaQuery, useTheme, Chip, IconButton, Switch, FormControlLabel
} from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import axiosInstance from '../../../../lib/axios';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';

const TiffinDeliveryPerson = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const globalPropertyId = useSelector(selectGlobalProperty);
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [orderData, setOrderData] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isBulkDelivery, setIsBulkDelivery] = useState(false); // Add state for bulk delivery
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    
    const [formData, setFormData] = useState({
        enteredOtp: '',
        deliveryTime: dayjs(),
        deliveryRemark: '',
        deliveredQuantity: 1
    });

    useEffect(() => {
        const state = location.state;
        if (state && state.mode === 'delivery' && state.orderData) {
            setOrderData(state.orderData);
            setIsEditMode(state.isEdit || false);
            
            // If edit mode, skip OTP verification step
            if (state.isEdit) {
                setOtpVerified(true);
                setActiveStep(1);
            }
            
            setFormData(prev => ({
                ...prev,
                deliveredQuantity: state.orderData.Req_Qty || 1,
                deliveryTime: state.orderData.Dlv_Act_Time ? dayjs(state.orderData.Dlv_Act_Time) : dayjs(),
                deliveryRemark: state.orderData.Dlv_Remark || ''
            }));
        } else {
            toast.error('No order data found');
            navigate('/TiffinDeliveryPersontable');
        }
    }, [location.state, navigate]);

   

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'deliveredQuantity') {
            const newValue = parseInt(value) || 0;
            if (newValue > orderData?.Req_Qty) {
                setSnackbar({
                    open: true,
                    message: `Cannot deliver more than ${orderData.Req_Qty} tiffins`,
                    severity: 'error'
                });
                return;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };

   const handleDeliveryModeToggle = (event) => {
    const isBulk = event.target.checked;
    setIsBulkDelivery(isBulk);
    
    if (isBulk) {
        // Switching to bulk: skip OTP
        setOtpVerified(true);
        setActiveStep(1);
    } else {
        // Switching back to single: reset OTP state
        setOtpVerified(false);
        setActiveStep(0);
        setFormData(prev => ({ ...prev, enteredOtp: '' })); // Clear OTP input too
    }
};

    const handleVerifyOtp = async () => {
        if (!formData.enteredOtp || formData.enteredOtp.length !== 6) {
            setSnackbar({
                open: true,
                message: 'Please enter a valid 6-digit OTP',
                severity: 'error'
            });
            return;
        }

        setVerifyingOtp(true);
        try {
            if (formData.enteredOtp === orderData.Dlv_OTP) {
                setOtpVerified(true);
                setActiveStep(1);
                toast.success('OTP verified successfully!');
            } else {
                toast.error('OTP mismatch! Please check the OTP and try again.');
            }
        } catch (err) {
            console.error('Error verifying OTP:', err);
            setSnackbar({
                open: true,
                message: 'Error verifying OTP. Please try again.',
                severity: 'error'
            });
        } finally {
            setVerifyingOtp(false);
        }
    };

    const handleVerifyDelivery = async () => {
        // Skip OTP check for bulk delivery or edit mode
        if (!otpVerified && !isEditMode && !isBulkDelivery) {
            setSnackbar({
                open: true,
                message: 'Please verify OTP first before marking delivery',
                severity: 'error'
            });
            return;
        }

        setSubmitting(true);
        try {
            const UserId = sessionStorage?.getItem('UserId');
            if (!UserId) {
                setSnackbar({ open: true, message: 'User not found. Please login again.', severity: 'error' });
                return;
            }

            const payload = {
                Tfn_ReqId: orderData.Tfn_ReqId,
                Bulk_Dlv: isBulkDelivery ? "Y" : "N", // Set based on bulk delivery mode
                Dlv_LoginID: UserId,
                Dlv_Act_Time: formData.deliveryTime.format('YYYY-MM-DD HH:mm:ss'),
                Dlv_Remark: formData.deliveryRemark || "",
                Dlv_Qty: parseInt(formData.deliveredQuantity),
                UpdatedBy: parseInt(UserId)
            };

            const response = await axiosInstance.post('TrnTiffin/UpdateTrnTiffinDeliveryStatus', payload);

            if (response.data?.Status === 0 || response.data?.Success) {
                toast.success(isEditMode ? 'Delivery updated successfully!' : 
                             (isBulkDelivery ? 'Bulk delivery completed successfully!' : 'Order delivered successfully!'));
                
                if (formData.deliveredQuantity < orderData.Req_Qty) {
                    toast.warning(`Note: Only ${formData.deliveredQuantity} out of ${orderData.Req_Qty} tiffins delivered`);
                }
                
                setTimeout(() => {
                    navigate('/TiffinDeliveryPersontable');
                }, 2000);
            } else {
                throw new Error(response.data?.Message || 'Failed to verify delivery');
            }
        } catch (err) {
            console.error('Error verifying delivery:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.Message || err.message || 'Failed to verify delivery. Please try again.',
                severity: 'error'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formatTimeOnly = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    // Hide OTP with asterisks
    const maskOtp = (otp) => {
        if (!otp) return '******';
        return '*'.repeat(otp.length);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                minHeight: '100vh', 
                bgcolor: '#f5f7fa', 
                p: { xs: 1, sm: 2, md: 3, lg: 4 },
                position: 'relative'
            }}>
                <ToastContainer 
                    position={isMobile ? "bottom-center" : "top-right"}
                    style={{ fontSize: isMobile ? '12px' : '14px' }}
                />
                
                {/* Header Section */}
                <Box sx={{ maxWidth: 1400, mx: 'auto', mb: { xs: 2, sm: 3 } }}>
                    <Paper elevation={0} sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        borderRadius: { xs: 2, sm: 3 }, 
                        bgcolor: 'white', 
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)' 
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                                <IconButton 
                                    onClick={() => navigate('/TiffinDeliveryPersontable')}
                                    sx={{ 
                                        bgcolor: '#f0f0f0', 
                                        '&:hover': { bgcolor: '#e0e0e0' },
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 }
                                    }}
                                >
                                    <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                                </IconButton>
                                <Box>
                                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                        {isEditMode ? 'Edit Delivery' : 'Delivery Verification'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                                        {isEditMode ? 'Update delivery details' : 'Verify and complete delivery'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {/* Delivery Mode Switch - Only show for non-edit mode */}
                            {!isEditMode && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isBulkDelivery}
                                            onChange={handleDeliveryModeToggle}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#8b5cf6',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#8b5cf6',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {isBulkDelivery ? (
                                                <GroupsIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#8b5cf6' }} />
                                            ) : (
                                                <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#635bff' }} />
                                            )}
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '12px', sm: '14px' } }}>
                                                {isBulkDelivery ? "Bulk Delivery" : "Single Delivery"}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ mr: 0 }}
                                />
                            )}
                            
                            {!isEditMode && (
                                <Chip 
                                    icon={isBulkDelivery ? <GroupsIcon /> : <DeliveryDiningIcon />}
                                    label={isBulkDelivery ? 'Bulk Mode' : (isEditMode ? 'Edit Mode' : 'Single Mode')}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{ 
                                        bgcolor: isBulkDelivery ? '#ede9fe' : (isEditMode ? '#fef3c7' : '#dbeafe'),
                                        color: isBulkDelivery ? '#6d28d9' : (isEditMode ? '#d97706' : '#2563eb'),
                                        fontWeight: 600,
                                        px: 1,
                                        fontSize: { xs: '11px', sm: '13px' }
                                    }}
                                />
                            )}
                            
                            {isEditMode && (
                                <Chip 
                                    icon={<DeliveryDiningIcon />}
                                    label={'Edit Mode'}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{ 
                                        bgcolor: '#fef3c7',
                                        color: '#d97706',
                                        fontWeight: 600,
                                        px: 1,
                                        fontSize: { xs: '11px', sm: '13px' }
                                    }}
                                />
                            )}
                        </Box>
                        
                       
                    </Paper>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ maxWidth: 1400, mx: 'auto' }}>
                    {/* Left Column - Order Details */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ 
                            borderRadius: { xs: 2, sm: 3 }, 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                            position: { xs: 'relative', md: 'sticky' },
                            top: { md: 20 }
                        }}>
                            <Box sx={{ 
                                background: isBulkDelivery && !isEditMode 
                                    ? 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' 
                                    : 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
                                p: { xs: 2, sm: 3 },
                                color: 'white'
                            }}>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1, fontSize: { xs: '10px', sm: '12px' } }}>
                                    Order Information
                                </Typography>
                                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1 }}>
                                    #{orderData?.Tfn_ReqId}
                                </Typography>
                                <Chip 
                                    label={orderData?.Tfn_ReqSt}
                                    size="small"
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>

                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <SchoolIcon sx={{ color: '#635bff', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Institute</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.InstituteName}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <LocationOnIcon sx={{ color: '#f97316', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Delivery Location</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.Dlv_Loc}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <RestaurantIcon sx={{ color: '#d97706', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Meal Type</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.MealTypeName}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <AccessTimeIcon sx={{ color: '#3b82f6', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Delivery Time</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {formatTimeOnly(orderData?.Dlv_Time)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Order Quantity</Typography>
                                        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ color: '#635bff' }}>
                                            {orderData?.Req_Qty}
                                            <Typography component="span" variant="body2" color="text.secondary"> Tiffin(s)</Typography>
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Order OTP</Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontFamily: 'monospace', 
                                            fontWeight: 700, 
                                            letterSpacing: { xs: 2, sm: 4 },
                                            fontSize: { xs: '16px', sm: '18px' }
                                        }}>
                                            {isBulkDelivery && !isEditMode ? 'BULK MODE' : maskOtp(orderData?.Dlv_OTP)}
                                        </Typography>
                                        {isBulkDelivery && !isEditMode && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                                OTP not required for bulk delivery
                                            </Typography>
                                        )}
                                    </Grid>

                                    {orderData?.Req_Remark && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">Remarks</Typography>
                                            <Typography variant="body2" sx={{ 
                                                bgcolor: '#f3f4f6', 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                fontSize: { xs: '12px', sm: '14px' }
                                            }}>
                                                {orderData.Req_Remark}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column - Action Steps */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: { xs: 2, sm: 3 }, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Stepper activeStep={activeStep} orientation="vertical" sx={{ gap: 2 }}>
                                    {/* Step 1: OTP Verification - Skip for bulk delivery mode */}
                                    {!isEditMode && !isBulkDelivery && (
                                        <Step expanded={activeStep === 0}>
                                            <StepLabel StepIconComponent={() => (
                                                <Avatar sx={{ 
                                                    bgcolor: otpVerified ? '#22c55e' : '#635bff', 
                                                    width: { xs: 28, sm: 32 }, 
                                                    height: { xs: 28, sm: 32 } 
                                                }}>
                                                    <LockIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                                                </Avatar>
                                            )}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                                    OTP Verification
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '10px', sm: '12px' } }}>
                                                    Verify delivery with OTP
                                                </Typography>
                                            </StepLabel>
                                            <StepContent>
                                                <Box sx={{ 
                                                    mt: 2, 
                                                    display: 'flex', 
                                                    gap: 2, 
                                                    flexDirection: { xs: 'column', sm: 'row' } 
                                                }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Enter 6-digit OTP"
                                                        name="enteredOtp"
                                                        value={formData.enteredOtp}
                                                        onChange={handleChange}
                                                        disabled={otpVerified}
                                                        inputProps={{ maxLength: 6 }}
                                                        size={isMobile ? "small" : "medium"}
                                                        sx={{ flex: 2 }}
                                                        autoFocus
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleVerifyOtp}
                                                        disabled={otpVerified || verifyingOtp || !formData.enteredOtp}
                                                        size={isMobile ? "small" : "medium"}
                                                        sx={{ 
                                                            flex: 1,
                                                            bgcolor: '#635bff',
                                                            '&:hover': { bgcolor: '#524ccc' }
                                                        }}
                                                    >
                                                        {verifyingOtp ? <CircularProgress size={isMobile ? 20 : 24} /> : otpVerified ? 'Verified ✓' : 'Verify OTP'}
                                                    </Button>
                                                </Box>
                                            </StepContent>
                                        </Step>
                                    )}

                                    {/* Step 2: Delivery Confirmation */}
                                    <Step expanded={(!isEditMode && !isBulkDelivery && activeStep === 1) || (isBulkDelivery && !isEditMode) || isEditMode}>
                                        <StepLabel StepIconComponent={() => (
                                            <Avatar sx={{ 
                                                bgcolor: ((!isEditMode && !isBulkDelivery && activeStep >= 1) || isBulkDelivery || isEditMode) ? '#22c55e' : '#9ca3af', 
                                                width: { xs: 28, sm: 32 }, 
                                                height: { xs: 28, sm: 32 } 
                                            }}>
                                                <DeliveryDiningIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                                            </Avatar>
                                        )}>
                                            <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                                Delivery Confirmation
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '10px', sm: '12px' } }}>
                                                {isEditMode ? 'Update delivery details' : (isBulkDelivery ? 'Confirm bulk delivery' : 'Confirm delivery details')}
                                            </Typography>
                                        </StepLabel>
                                        <StepContent>
                                            <Box sx={{ mt: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <MobileDateTimePicker
                                                            label="Delivery Time"
                                                            value={formData.deliveryTime}
                                                            onChange={(newTime) => setFormData(prev => ({ ...prev, deliveryTime: newTime }))}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    size: isMobile ? "small" : "medium"
                                                                }
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            size={isMobile ? "small" : "medium"}
                                                            label="Delivered Quantity"
                                                            name="deliveredQuantity"
                                                            type="number"
                                                            value={formData.deliveredQuantity}
                                                            onChange={handleChange}
                                                            inputProps={{ min: 0, max: orderData?.Req_Qty }}
                                                            helperText={`Max: ${orderData?.Req_Qty} tiffin(s)`}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            size={isMobile ? "small" : "medium"}
                                                            label="Delivery Remarks"
                                                            name="deliveryRemark"
                                                            value={formData.deliveryRemark}
                                                            onChange={handleChange}
                                                            multiline
                                                            rows={2}
                                                            placeholder="Any special notes about delivery..."
                                                        />
                                                    </Grid>

                                                    {formData.deliveredQuantity < orderData?.Req_Qty && (
                                                        <Grid item xs={12}>
                                                            <Alert severity="warning" icon={<WarningAmberIcon />}>
                                                                <Typography variant="body2" sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                                                                    <strong>Short Delivery Alert:</strong> You are delivering only {formData.deliveredQuantity} out of {orderData.Req_Qty} tiffins.
                                                                </Typography>
                                                            </Alert>
                                                        </Grid>
                                                    )}

                                                    <Grid item xs={12}>
                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            onClick={handleVerifyDelivery}
                                                            disabled={submitting}
                                                            size={isMobile ? "large" : "medium"}
                                                            sx={{
                                                                py: { xs: 1, sm: 1.5 },
                                                                bgcolor: isBulkDelivery ? '#8b5cf6' : '#22c55e',
                                                                '&:hover': { 
                                                                    bgcolor: isBulkDelivery ? '#6d28d9' : '#16a34a' 
                                                                }
                                                            }}
                                                        >
                                                            {submitting ? <CircularProgress size={24} /> : (isEditMode ? '✓ Update Delivery' : (isBulkDelivery ? '✓ Confirm Bulk Delivery' : '✓ Confirm Delivery'))}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                </Stepper>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ 
                        vertical: isMobile ? 'bottom' : 'top', 
                        horizontal: isMobile ? 'center' : 'right' 
                    }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default TiffinDeliveryPerson;